import { useState, useEffect, useRef, useCallback } from "react";
import { BuildingCheckResponse, ExtractedContract, SessionConfirmedData, BuildingData, OwnerData } from "../../types/contract";
import apiClient from "../../api/axios";
import { normalizeName } from "../../utils/normalization";
import { findBestMatchingBuilding } from "../../utils/matching";

// 家主比較ロジック（抽出結果の家主情報と既存DBの家主情報を比較）
const compareOwner = (
  existingOwner: OwnerData | undefined,
  contract: ExtractedContract
): { type: 'exact' | 'suggest' | 'none'; reason?: string } => {
  if (!existingOwner) return { type: 'none' };

  const newName = contract.owner_name?.value;
  const newKana = contract.owner_name_kana?.value;
  const newPhone = contract.owner_home_phone?.value;
  const newAddress = contract.owner_address1?.value;

  // 完全一致判定（簡易版の正規化）
  if (existingOwner.name === newName) {
    return { type: 'exact' };
  }

  // サジェスト判定: カナ
  if (newKana && existingOwner.name_kana) {
    if (existingOwner.name_kana === newKana) {
      return { type: 'suggest', reason: 'カナ名が一致' };
    }
  }

  // サジェスト判定: 電話番号
  if (newPhone && existingOwner.home_phone) {
    if (existingOwner.home_phone === newPhone) {
      return { type: 'suggest', reason: '電話番号が一致' };
    }
  }

  // サジェスト判定: 住所
  if (newAddress && existingOwner.address1) {
    if (existingOwner.address1 === newAddress) {
      return { type: 'suggest', reason: '住所が一致' };
    }
  }

  return { type: 'none' };
};

export const useBuildingCheck = (
  finalContracts: ExtractedContract[],
  flowState: string,
  selectedContract: ExtractedContract | null,
  sessionConfirmedData: SessionConfirmedData,
  setBeforeExistingDataMap: (updater: (prev: Map<number, ExtractedContract>) => Map<number, ExtractedContract>) => void,
  dbCheckedContracts: Set<number>,
  setDbCheckedContracts: (updater: (prev: Set<number>) => Set<number>) => void,
  setConflictResolutions: (updater: (prev: Map<number, { building: 'keep' | 'update' | null, owner: 'keep' | 'update' | null }>) => Map<number, { building: 'keep' | 'update' | null, owner: 'keep' | 'update' | null }>) => void,
  applyExistingData: (contractIdx: number, applyBuilding: boolean, applyOwner: boolean) => void
) => {
  // 重複チェック用のState
  const [buildingChecks, setBuildingChecks] = useState<Map<number, BuildingCheckResponse>>(new Map());
  const hasCheckedBuildingsRef = useRef(false);

  // 物件住所の重複チェック（抽出結果に対して一度だけ実行）
  const handlePropertyAddressChange = useCallback(async (contractIdx: number, contract: ExtractedContract) => {
    const address1 = contract.property_address?.value;
    const buildingName = contract.property_name;

    if (!address1) {
      setBuildingChecks((prev) => {
        const newMap = new Map(prev);
        newMap.delete(contractIdx);
        return newMap;
      });
      return;
    }

    try {
      // 1. DB からの照合（初回のみ）
      let dbMatch: BuildingCheckResponse | null = null;

      if (!dbCheckedContracts.has(contractIdx)) {
        // 初回：DB照合を実行
        try {
          const ownerName = contract.owner_name?.value;
          const ownerKana = contract.owner_name_kana?.value;

          const response = await apiClient.get<BuildingCheckResponse>(
            '/api/check-building-by-address/',
            {
              params: {
                address1,
                building_name: buildingName,
                owner_name: ownerName,
                owner_kana: ownerKana
              }
            }
          );

          if (response.data.found && response.data.building) {
            // 家主比較（フロントエンドで判定）
            const ownerMatchResult = compareOwner(response.data.owner, contract);
            dbMatch = {
              ...response.data,
              owner_match_type: ownerMatchResult.type,
              match_reason: ownerMatchResult.reason,
            };
          }

          // DB照合済みとしてマーク
          setDbCheckedContracts(prev => new Set(prev).add(contractIdx));
        } catch (error) {
          console.error('DB 照合エラー:', error);
          // エラーでも照合済みとしてマーク（再試行を防ぐ）
          setDbCheckedContracts(prev => new Set(prev).add(contractIdx));
        }
      } else {
        // 2回目以降：既存のDB結果を再利用
        const existingCheck = buildingChecks.get(contractIdx);
        if (existingCheck && existingCheck.data_source === 'confirmed') {
          dbMatch = existingCheck;
        }
      }

      // 2. sessionConfirmedData からも照合（バックエンドと同じロジック）
      const [sessionBuilding, sessionMatchType] = findBestMatchingBuilding(
        contract,
        sessionConfirmedData.buildings,
        contract.id  // 契約IDを渡す（インデックスではない）
      );

      // 3. 2つの候補を比較してベストマッチを選択
      let finalMatch: BuildingCheckResponse | null = null;

      // 優先順位の判定（match_type と owner一致度で判断）
      // 優先順位: both > name > address
      const matchTypePriority = { both: 3, name: 2, address: 1 };

      if (dbMatch && sessionBuilding) {
        // Building の一致度
        const dbBuildingPriority = matchTypePriority[dbMatch.building_match_type || 'address'];
        const sessionBuildingPriority = matchTypePriority[sessionMatchType || 'address'];

        // Owner の一致度を計算
        const extractedOwnerName = contract.owner_name?.value || '';
        const normalizedExtractedOwnerName = normalizeName(extractedOwnerName);

        // DB match の owner 一致度
        let dbOwnerPriority = 1; // none
        if (dbMatch.owner) {
          const normalizedDbOwnerName = normalizeName(dbMatch.owner.name);
          if (normalizedDbOwnerName === normalizedExtractedOwnerName) {
            dbOwnerPriority = 3; // exact
          } else if (dbMatch.owner_match_type === 'suggest') {
            dbOwnerPriority = 2; // suggest
          }
        }

        // Session match の owner 一致度
        let sessionOwnerPriority = 1; // none
        if (sessionBuilding.owner.name) {
          const normalizedSessionOwnerName = sessionBuilding.owner.name_normalized || normalizeName(sessionBuilding.owner.name);
          if (normalizedSessionOwnerName === normalizedExtractedOwnerName) {
            sessionOwnerPriority = 3; // exact
          }
        }

        // 総合優先度 = Building優先度 * 10 + Owner優先度
        // これにより、Building一致度を優先しつつ、Owner一致度も考慮
        const dbTotalPriority = dbBuildingPriority * 10 + dbOwnerPriority;
        const sessionTotalPriority = sessionBuildingPriority * 10 + sessionOwnerPriority;

        // 総合優先度が高い方を選択（同点ならDB既存を優先）
        if (sessionTotalPriority > dbTotalPriority) {
          finalMatch = {
            found: true,
            building: {
              id: 0,
              owner_id: 0,
              name: sessionBuilding.name,
              name_kana: sessionBuilding.name_kana ?? null,
              building_code: sessionBuilding.building_code ?? null,
              postal_code: sessionBuilding.postal_code ?? null,
              address1: sessionBuilding.address1 ?? null,
              address2: sessionBuilding.address2 ?? null,
              owner_name: sessionBuilding.owner.name ?? null,
            } as BuildingData,
            owner: sessionBuilding.owner.name ? {
              id: 0,
              legacy_id: null,
              name: sessionBuilding.owner.name ?? null,
              name_kana: sessionBuilding.owner.name_kana ?? null,
              postal_code: sessionBuilding.owner.postal_code ?? null,
              address1: sessionBuilding.owner.address1 ?? null,
              address2: sessionBuilding.owner.address2 ?? null,
              home_phone: sessionBuilding.owner.home_phone ?? null,
              mobile_phone: sessionBuilding.owner.mobile_phone ?? null,
              bank_1_branch_code: sessionBuilding.owner.bank_1_branch_code ?? null,
              bank_1_account_type: sessionBuilding.owner.bank_1_account_type ?? null,
              bank_1_account_number: sessionBuilding.owner.bank_1_account_number ?? null,
              bank_1_account_holder_name: sessionBuilding.owner.bank_1_account_holder_name ?? null,
              bank_2_branch_code: sessionBuilding.owner.bank_2_branch_code ?? null,
              bank_2_account_type: sessionBuilding.owner.bank_2_account_type ?? null,
              bank_2_account_number: sessionBuilding.owner.bank_2_account_number ?? null,
              bank_2_account_holder_name: sessionBuilding.owner.bank_2_account_holder_name ?? null,
            } as OwnerData : undefined,
            building_match_type: sessionMatchType ?? undefined,
            owner_match_type: sessionBuilding.owner.name ? 'suggest' : 'none',
            match_reason: `セッション内データと一致`,
            data_source: 'draft',
            session_temp_id: sessionBuilding.tempId,
          };
        } else {
          finalMatch = {
            ...dbMatch,
            data_source: 'confirmed'
          };
        }
      } else if (dbMatch) {
        finalMatch = {
          ...dbMatch,
          data_source: 'confirmed'
        };
      } else if (sessionBuilding) {
        finalMatch = {
          found: true,
          building: {
            id: 0,
            owner_id: 0,
            name: sessionBuilding.name,
            name_kana: sessionBuilding.name_kana ?? null,
            building_code: sessionBuilding.building_code ?? null,
            postal_code: sessionBuilding.postal_code ?? null,
            address1: sessionBuilding.address1 ?? null,
            address2: sessionBuilding.address2 ?? null,
            owner_name: sessionBuilding.owner.name ?? null,
          } as BuildingData,
          owner: sessionBuilding.owner.name ? {
            id: 0,
            legacy_id: null,
            name: sessionBuilding.owner.name ?? null,
            name_kana: sessionBuilding.owner.name_kana ?? null,
            postal_code: sessionBuilding.owner.postal_code ?? null,
            address1: sessionBuilding.owner.address1 ?? null,
            address2: sessionBuilding.owner.address2 ?? null,
            home_phone: sessionBuilding.owner.home_phone ?? null,
            mobile_phone: sessionBuilding.owner.mobile_phone ?? null,
            bank_1_branch_code: sessionBuilding.owner.bank_1_branch_code ?? null,
            bank_1_account_type: sessionBuilding.owner.bank_1_account_type ?? null,
            bank_1_account_number: sessionBuilding.owner.bank_1_account_number ?? null,
            bank_1_account_holder_name: sessionBuilding.owner.bank_1_account_holder_name ?? null,
            bank_2_branch_code: sessionBuilding.owner.bank_2_branch_code ?? null,
            bank_2_account_type: sessionBuilding.owner.bank_2_account_type ?? null,
            bank_2_account_number: sessionBuilding.owner.bank_2_account_number ?? null,
            bank_2_account_holder_name: sessionBuilding.owner.bank_2_account_holder_name ?? null,
          } as OwnerData : undefined,
          building_match_type: sessionMatchType ?? undefined,
          owner_match_type: sessionBuilding.owner.name ? 'suggest' : 'none',
          match_reason: `セッション内データと一致`,
          data_source: 'draft',
          session_temp_id: sessionBuilding.tempId,
        };
      }

      // 4. 結果を保存
      if (finalMatch) {
        setBuildingChecks((prev) => new Map(prev).set(contractIdx, finalMatch as BuildingCheckResponse));

        // デフォルト選択を設定（初回のみ）
        setConflictResolutions((prev) => {
          if (!prev.has(contractIdx)) {
            // 予測ロジック：正規化後の名前が一致するか判定
            const currentContract = contract;

            // 建物の判定
            const normalizedCurrentBuildingName = normalizeName(currentContract.property_name);
            const normalizedExistingBuildingName = finalMatch?.building?.name || '';
            const buildingWillUpdate = normalizedCurrentBuildingName === normalizedExistingBuildingName;

            // 家主の判定
            let ownerWillUpdate = false;
            if (finalMatch?.owner) {
              const normalizedCurrentOwnerName = normalizeName(
                typeof currentContract.owner_name === 'string'
                  ? currentContract.owner_name
                  : currentContract.owner_name?.value || ''
              );
              const normalizedExistingOwnerName = finalMatch.owner.name || '';
              ownerWillUpdate = normalizedCurrentOwnerName === normalizedExistingOwnerName;
            }

            // デフォルト選択のルール（折衷案）：
            // - 更新となる場合（名前一致） → 'update'（抽出・編集結果を採用）
            // - 新規登録となる場合（名前不一致） → null（未選択、ユーザーに明示的な選択を促す）

            // 建物の判定
            let buildingDefault: 'keep' | 'update' | null;
            if (ownerWillUpdate && buildingWillUpdate) {
              // 両方一致 → 更新なので抽出・編集結果をデフォルト
              buildingDefault = 'update';
            } else {
              // どちらか不一致 → 新規登録になるので未選択
              buildingDefault = null;
            }

            // 家主の判定
            const ownerDefault: 'keep' | 'update' | null = ownerWillUpdate ? 'update' : null;

            const newMap = new Map(prev);
            newMap.set(contractIdx, { building: buildingDefault, owner: ownerDefault });

            // 自動的にデフォルト選択を適用（nullでない場合のみ）
            if (buildingDefault !== null && ownerDefault !== null) {
              setTimeout(() => {
                // デフォルトは'update'のみなので、既存データは適用しない（false）
                const applyBuilding = false;
                const applyOwner = false;
                applyExistingData(contractIdx, applyBuilding, applyOwner);
              }, 0);
            }

            return newMap;
          }
          return prev;
        });
      } else {
        setBuildingChecks((prev) => {
          const newMap = new Map(prev);
          newMap.delete(contractIdx);
          return newMap;
        });
      }
    } catch (error) {
      console.error('照合エラー:', error);
    }
  }, [
    buildingChecks,
    dbCheckedContracts,
    setDbCheckedContracts,
    sessionConfirmedData,
    setBuildingChecks,
    setConflictResolutions,
    applyExistingData,
  ]);

  // 抽出結果が得られたときに一度だけ重複チェックを実行
  useEffect(() => {
    if (finalContracts.length > 0 && !hasCheckedBuildingsRef.current && flowState === "detailedExtracted") {
      hasCheckedBuildingsRef.current = true;

      // 全ての契約に対してチェックを実行
      finalContracts.forEach((contract, idx) => {
        handlePropertyAddressChange(idx, contract);
      });
    }
  }, [finalContracts, flowState, handlePropertyAddressChange]);

  // flowStateがリセットされたら、チェックフラグもリセット
  useEffect(() => {
    if (flowState === "initial") {
      hasCheckedBuildingsRef.current = false;
      setBuildingChecks(new Map());
      setBeforeExistingDataMap(() => new Map());
    }
  }, [flowState, setBeforeExistingDataMap]);

  // 契約選択時または照合に影響する項目が変更されたときに照合処理を再実行
  const lastCheckKeyRef = useRef<string>("");

  useEffect(() => {
    if (selectedContract && selectedContract.id !== undefined && flowState === "detailedExtracted") {
      const contractIdx = finalContracts.findIndex(c => c.id === selectedContract.id);
      if (contractIdx !== -1) {
        // 照合結果に影響する項目のみをキーにする
        const ownerName = typeof selectedContract.owner_name === 'string' 
          ? selectedContract.owner_name 
          : selectedContract.owner_name?.value || '';
        
        const currentCheckKey = `${selectedContract.id}-${selectedContract.property_name}-${selectedContract.property_address?.value}-${ownerName}`;
        
        if (lastCheckKeyRef.current !== currentCheckKey) {
          lastCheckKeyRef.current = currentCheckKey;
          handlePropertyAddressChange(contractIdx, selectedContract);
        }
      }
    }
  }, [selectedContract, sessionConfirmedData.buildings.length, flowState, finalContracts, handlePropertyAddressChange]);

  // リセット関数
  const resetBuildingState = useCallback(() => {
    setBuildingChecks(new Map());
    hasCheckedBuildingsRef.current = false;
  }, []);

  return {
    buildingChecks,
    setBuildingChecks,
    hasCheckedBuildingsRef,
    resetBuildingState,
    handlePropertyAddressChange,
  };
};